import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Redirect, Link, useHistory, useParams } from 'react-router-dom'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import {
  Spin,
  Row,
  Col,
  Button,
  Input,
  Breadcrumb,
  Form,
  Modal,
  Table,
  Select,
  Switch,
} from 'antd'
import PhoneInput, { formatPhoneNumberIntl } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import '../../css/surgeon.scss'
import '../../css/users.scss'
import util from '../../util/util'
import { User } from '../../component'
import {
  fetchCompany,
  addEditSupportProfile,
  deleteSupportProfile,
} from '../../store/action/company.action'

const { Table: UserTable } = User

const CompanyDetail = (props) => {
  const [company, setCompany] = useState(undefined)
  const [modalVisible, setModalVisible] = useState(false)
  const [editing, setEditing] = useState(undefined)
  const [form] = Form.useForm()

  var history = useHistory()
  var params = useParams()
  var companyid = params.id

  useEffect(() => {
    getCompany()
  }, [])

  var getCompany = async () => {
    if (!companyid) return
    var response = await props.getCompany(companyid)
    if (!response) return
    setCompany(response)
  }

  // ----- Support profile CRUD -----
  var openAddSupport = () => {
    setEditing(undefined)
    form.setFieldsValue({
      name: '',
      email: '',
      phone: '',
      phonesupport: true,
      timefrom: '',
      timeto: '',
      timezone: '',
    })
    setModalVisible(true)
  }

  var openEditSupport = (record) => {
    setEditing(record)
    form.setFieldsValue({
      name: record.name,
      email: record.email,
      phone: record.phone
        ? record.phone.charAt(0) == '+'
          ? record.phone
          : '+' + record.phone
        : '',
      // Rows created before the flag existed have it undefined, which means on.
      phonesupport: record.phonesupport !== false,
      timefrom: record.timefrom,
      timeto: record.timeto,
      timezone: record.timezone,
    })
    setModalVisible(true)
  }

  var handleSupportSubmit = async (value) => {
    var input = {
      companyid,
      name: value.name,
      email: value.email,
      phone: value.phone || '',
      phonesupport: value.phonesupport !== false,
      timefrom: value.timefrom || '',
      timeto: value.timeto || '',
      timezone: value.timezone || '',
    }
    if (editing) input._id = editing._id
    var response = await props.addEditSupportProfile(input)
    if (!response) return
    setModalVisible(false)
    getCompany()
  }

  var handleSupportDelete = (record) => {
    Modal.confirm({
      title: 'Delete support profile',
      icon: <ExclamationCircleOutlined />,
      content:
        'Are you sure you want to delete the support profile "' +
        (record.name || '') +
        '"?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        var response = await props.deleteSupportProfile(companyid, record._id)
        if (response) getCompany()
      },
    })
  }

  var supportColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'Support Email',
      dataIndex: 'email',
    },
    {
      title: 'Support Phone',
      dataIndex: 'phone',
      render: (phone, record) => {
        var formatted = formatPhoneNumberIntl(phone ? phone : '')
        if (record.phonesupport === false) {
          // Number is kept but withheld from patients.
          return (
            <span style={{ color: '#8c8c8c' }}>
              {formatted} <i>(off)</i>
            </span>
          )
        }
        return formatted
      },
    },
    {
      title: 'Support Hours',
      render: (row, record) =>
        record.timefrom && record.timeto
          ? record.timefrom +
            ' to ' +
            record.timeto +
            (record.timezone ? ' ' + record.timezone : '')
          : '',
    },
    {
      title: 'ACTION',
      align: 'center',
      width: '200px',
      render: (row, record) => (
        <Row justify={'center'} align={'middle'} gutter={16}>
          <Col>
            <a onClick={() => openEditSupport(record)}>Edit</a>
          </Col>
          <Col>
            <a
              style={{ color: '#ff4d4f' }}
              onClick={() => handleSupportDelete(record)}
            >
              Delete
            </a>
          </Col>
        </Row>
      ),
    },
  ]

  var supportData = (company && company.supportprofiles) || []
  supportData.forEach((e) => (e.key = e._id))

  return props.token == undefined ? (
    <Redirect to='/login' />
  ) : (
    <Spin spinning={props.isLoading}>
      <Col className='surgeonAddContainer'>
        <Row>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/companies'>Companies</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              {company ? company.name : 'Company'}
            </Breadcrumb.Item>
          </Breadcrumb>
        </Row>

        {/* ---- Support Profile section ---- */}
        <Row align='middle' className='actionRow' justify='space-between'>
          <Col className='headerText'>Support Profile</Col>
          <Col>
            <Button className={'actionBtn'} onClick={openAddSupport}>
              ADD SUPPORT
            </Button>
          </Col>
        </Row>
        <Table
          size={'large'}
          className={'custom-table'}
          columns={supportColumns}
          dataSource={supportData}
          pagination={false}
          bordered={false}
        />

        {/* ---- Company Users section ---- */}
        <Row
          align='middle'
          className='actionRow'
          justify='space-between'
          style={{ marginTop: 30 }}
        >
          <Col className='headerText'>Company Users</Col>
          <Col>
            <Button
              className={'actionBtn'}
              onClick={() =>
                history.push('/companies/' + companyid + '/users/add')
              }
            >
              ADD USER
            </Button>
          </Col>
        </Row>
        <UserTable back={'/companies/' + companyid + '/users'} showActions />

        {/* ---- Support add/edit modal ---- */}
        <Modal
          title={editing ? 'Edit Support Profile' : 'Add Support Profile'}
          visible={modalVisible}
          okText={editing ? 'Update' : 'Add'}
          onOk={() => form.submit()}
          onCancel={() => setModalVisible(false)}
          destroyOnClose
        >
          <Form form={form} layout={'vertical'} onFinish={handleSupportSubmit}>
            <Form.Item
              label='Name'
              name='name'
              rules={[{ required: true, message: 'Please enter name' }]}
            >
              <Input maxLength={50} />
            </Form.Item>
            <Form.Item
              label='Support Email'
              name='email'
              rules={[{ type: 'email', message: 'Please enter a valid email' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item label='Support Phone' name='phone'>
              <PhoneInput
                className='phone-input'
                defaultCountry={'US'}
                enableAreaCodes
              />
            </Form.Item>
            <Form.Item
              label='Phone Support'
              name='phonesupport'
              valuePropName='checked'
              initialValue={true}
              extra='When off, patients are not shown a phone number. The number above is kept.'
            >
              <Switch checkedChildren='On' unCheckedChildren='Off' />
            </Form.Item>
            <Row gutter={12}>
              <Col flex='50%'>
                <Form.Item label='Support Hours - From' name='timefrom'>
                  <Input placeholder='e.g. 10AM' maxLength={20} />
                </Form.Item>
              </Col>
              <Col flex='50%'>
                <Form.Item label='Support Hours - To' name='timeto'>
                  <Input placeholder='e.g. 3PM' maxLength={20} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label='Time Zone' name='timezone'>
              <Select allowClear placeholder='Select time zone'>
                <Select.Option value='Eastern Time'>Eastern Time</Select.Option>
                <Select.Option value='Central Time'>Central Time</Select.Option>
                <Select.Option value='Mountain Time'>
                  Mountain Time
                </Select.Option>
                <Select.Option value='Pacific Time'>Pacific Time</Select.Option>
                <Select.Option value='Alaska Time'>Alaska Time</Select.Option>
                <Select.Option value='Hawaii Time'>Hawaii Time</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </Col>
    </Spin>
  )
}

CompanyDetail.propTypes = {
  isLoading: PropTypes.bool,
  token: PropTypes.string,
}

const mapStateToProps = (state) => {
  return {
    token: util.verifyToken(state.profile.token),
    isLoading: state.profile.isLoading,
    company: state.profile.company,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    getCompany: (companyid) => dispatch(fetchCompany(companyid)),
    addEditSupportProfile: (input) => dispatch(addEditSupportProfile(input)),
    deleteSupportProfile: (companyid, id) =>
      dispatch(deleteSupportProfile(companyid, id)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(CompanyDetail)
